<script>
/* global axios */
/*
 * Account-level setup for campaign revenue tracking. Mounted in Relatórios >
 * Campanhas (seção Vendas), where the user reads the Vendas column, and still
 * next to the old WhatsApp reports. The settings apply to every campaign channel.
 *
 * Secrets are write-only: the API returns them masked, and an empty field means
 * "keep what is stored", so saving the form never wipes a working credential.
 */
import Button from 'dashboard/components-next/button/Button.vue';

// Chaves estáticas (eslint @intlify/vue-i18n/no-dynamic-keys): cada provedor
// aponta para as próprias chaves em vez de montar o caminho em tempo de execução.
const PROVIDERS = {
  generic: {
    label: 'REPORT.CAMPAIGNS.SALES_SETTINGS.PROVIDER.GENERIC',
    hint: 'REPORT.CAMPAIGNS.SALES_SETTINGS.PROVIDER.GENERIC_HINT',
  },
  pagarme: {
    label: 'REPORT.CAMPAIGNS.SALES_SETTINGS.PROVIDER.PAGARME',
    hint: 'REPORT.CAMPAIGNS.SALES_SETTINGS.PROVIDER.PAGARME_HINT',
  },
  stripe: {
    label: 'REPORT.CAMPAIGNS.SALES_SETTINGS.PROVIDER.STRIPE',
    hint: 'REPORT.CAMPAIGNS.SALES_SETTINGS.PROVIDER.STRIPE_HINT',
  },
};

const FIELD_LABELS = {
  token: 'REPORT.CAMPAIGNS.SALES_SETTINGS.FIELD.TOKEN',
  basic_user_optional: 'REPORT.CAMPAIGNS.SALES_SETTINGS.FIELD.USER_OPTIONAL',
  basic_password_optional:
    'REPORT.CAMPAIGNS.SALES_SETTINGS.FIELD.PASSWORD_OPTIONAL',
  basic_user: 'REPORT.CAMPAIGNS.SALES_SETTINGS.FIELD.BASIC_USER',
  basic_password: 'REPORT.CAMPAIGNS.SALES_SETTINGS.FIELD.BASIC_PASSWORD',
  hmac_secret: 'REPORT.CAMPAIGNS.SALES_SETTINGS.FIELD.HMAC_SECRET',
  signing_secret: 'REPORT.CAMPAIGNS.SALES_SETTINGS.FIELD.SIGNING_SECRET',
};

const CREDENTIAL_FIELDS = {
  generic: [
    { key: 'token', label: FIELD_LABELS.token, secret: true },
    { key: 'basic_user', label: FIELD_LABELS.basic_user_optional },
    {
      key: 'basic_password',
      label: FIELD_LABELS.basic_password_optional,
      secret: true,
    },
  ],
  pagarme: [
    { key: 'token', label: FIELD_LABELS.token, secret: true },
    { key: 'basic_user', label: FIELD_LABELS.basic_user },
    {
      key: 'basic_password',
      label: FIELD_LABELS.basic_password,
      secret: true,
    },
    { key: 'hmac_secret', label: FIELD_LABELS.hmac_secret, secret: true },
  ],
  stripe: [
    { key: 'signing_secret', label: FIELD_LABELS.signing_secret, secret: true },
  ],
};

export default {
  name: 'RevenueTrackingSettings',
  components: { Button },

  emits: ['saved'],

  data() {
    return {
      loading: true,
      saving: false,
      error: null,
      notice: null,
      settings: null,
      form: {
        enabled: false,
        currency: 'BRL',
        attribution_window_days: 7,
      },
      providerForm: {},
      revealedSecret: null,
    };
  },

  computed: {
    accountId() {
      return this.$route.params.accountId;
    },

    providers() {
      if (!this.settings) return [];
      return Object.keys(this.settings.providers || {}).map(key => ({
        key,
        label: PROVIDERS[key] ? this.$t(PROVIDERS[key].label) : key,
        hint: PROVIDERS[key] ? this.$t(PROVIDERS[key].hint) : '',
        fields: CREDENTIAL_FIELDS[key] || [],
        ...this.settings.providers[key],
      }));
    },
  },

  mounted() {
    this.fetchSettings();
  },

  methods: {
    settingsUrl(suffix = '') {
      return `/api/v1/accounts/${this.accountId}/revenue_tracking_settings${suffix}`;
    },

    applySettings(data) {
      this.settings = data;
      this.form = {
        enabled: data.enabled,
        currency: data.currency,
        attribution_window_days: data.attribution_window_days,
      };
      this.providerForm = Object.keys(data.providers || {}).reduce(
        (acc, key) => {
          acc[key] = { enabled: data.providers[key].enabled };
          (CREDENTIAL_FIELDS[key] || []).forEach(field => {
            acc[key][field.key] = '';
          });
          return acc;
        },
        {}
      );
    },

    async fetchSettings() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get(this.settingsUrl());
        this.applySettings(data);
      } catch (err) {
        this.error = this.$t('REPORT.CAMPAIGNS.SALES_SETTINGS.LOAD_ERROR');
      } finally {
        this.loading = false;
      }
    },

    async save() {
      this.saving = true;
      this.error = null;
      this.notice = null;
      try {
        const { data } = await axios.put(this.settingsUrl(), {
          revenue_tracking: { ...this.form, providers: this.providerForm },
        });
        this.applySettings(data);
        this.notice = this.$t('REPORT.CAMPAIGNS.SALES_SETTINGS.SAVED');
        this.$emit('saved', data);
      } catch (err) {
        this.error = this.$t('REPORT.CAMPAIGNS.SALES_SETTINGS.SAVE_ERROR');
      } finally {
        this.saving = false;
      }
    },

    async rotateSecret(provider) {
      this.error = null;
      this.notice = null;
      try {
        const { data } = await axios.post(this.settingsUrl('/rotate_secret'), {
          provider,
        });
        this.revealedSecret = { provider, secret: data.secret };
        await this.fetchSettings();
      } catch (err) {
        this.error = this.$t('REPORT.CAMPAIGNS.SALES_SETTINGS.ROTATE_ERROR');
      }
    },

    async copy(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.notice = this.$t('REPORT.CAMPAIGNS.SALES_SETTINGS.COPIED');
      } catch (err) {
        this.error = this.$t('REPORT.CAMPAIGNS.SALES_SETTINGS.COPY_ERROR');
      }
    },

    maskedValue(provider, fieldKey) {
      return (
        this.settings?.providers?.[provider]?.credentials?.[fieldKey] || ''
      );
    },
  },
};
</script>

<template>
  <div
    class="rounded-2xl border border-n-weak bg-n-surface-1 p-5 flex flex-col gap-4 text-sm"
  >
    <div>
      <h3 class="text-sm font-semibold text-n-slate-12 m-0">
        {{ $t('REPORT.CAMPAIGNS.SALES_SETTINGS.TITLE') }}
      </h3>
      <p class="text-xs text-n-slate-11 mt-1 mb-0">
        {{ $t('REPORT.CAMPAIGNS.SALES_SETTINGS.DESCRIPTION') }}
      </p>
    </div>

    <div v-if="loading" class="text-n-slate-11 text-xs" role="status">
      {{ $t('REPORT.CAMPAIGNS.SALES_SETTINGS.LOADING') }}
    </div>

    <div
      v-else-if="!settings && error"
      class="flex flex-wrap items-center gap-3 text-xs text-n-ruby-11"
      role="alert"
    >
      <span>{{ error }}</span>
      <Button
        :label="$t('REPORT.CAMPAIGNS.RETRY')"
        variant="faded"
        color="slate"
        size="sm"
        @click="fetchSettings"
      />
    </div>

    <template v-else-if="settings">
      <div
        v-if="error"
        class="text-xs text-n-ruby-11 bg-n-ruby-3 rounded-xl p-2"
        role="alert"
      >
        {{ error }}
      </div>
      <div
        v-if="notice"
        class="text-xs text-n-teal-11 bg-n-teal-3 rounded-xl p-2"
        role="status"
      >
        {{ notice }}
      </div>

      <label class="flex items-center gap-2 cursor-pointer min-h-11">
        <input v-model="form.enabled" type="checkbox" class="!mb-0" />
        <span class="text-n-slate-12">
          {{ $t('REPORT.CAMPAIGNS.SALES_SETTINGS.ENABLE') }}
        </span>
      </label>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">
            {{ $t('REPORT.CAMPAIGNS.SALES_SETTINGS.CURRENCY') }}
          </span>
          <input v-model="form.currency" type="text" maxlength="3" />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">
            {{ $t('REPORT.CAMPAIGNS.SALES_SETTINGS.WINDOW') }}
          </span>
          <input
            v-model.number="form.attribution_window_days"
            type="number"
            min="1"
            max="90"
          />
          <span class="text-[0.6875rem] text-n-slate-11">
            {{ $t('REPORT.CAMPAIGNS.SALES_SETTINGS.WINDOW_HINT') }}
          </span>
        </label>
      </div>

      <div
        v-for="provider in providers"
        :key="provider.key"
        class="rounded-xl border border-n-weak p-3 flex flex-col gap-2"
      >
        <label class="flex items-center gap-2 cursor-pointer min-h-11">
          <input
            v-model="providerForm[provider.key].enabled"
            type="checkbox"
            class="!mb-0"
          />
          <span class="font-semibold text-n-slate-12">{{
            provider.label
          }}</span>
        </label>
        <p class="text-[0.6875rem] text-n-slate-11 m-0">{{ provider.hint }}</p>

        <div class="flex items-center gap-2">
          <input
            :value="provider.webhook_url"
            type="text"
            readonly
            :aria-label="$t('REPORT.CAMPAIGNS.SALES_SETTINGS.WEBHOOK_URL')"
            class="!mb-0 font-mono text-xs"
          />
          <Button
            :label="$t('REPORT.CAMPAIGNS.SALES_SETTINGS.COPY')"
            variant="faded"
            color="slate"
            size="sm"
            icon="i-ri-file-copy-line"
            class="shrink-0"
            @click="copy(provider.webhook_url)"
          />
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
          <label
            v-for="field in provider.fields"
            :key="field.key"
            class="flex flex-col gap-1"
          >
            <span class="text-xs text-n-slate-11">{{ $t(field.label) }}</span>
            <input
              v-model="providerForm[provider.key][field.key]"
              type="text"
              :placeholder="
                maskedValue(provider.key, field.key) ||
                $t('REPORT.CAMPAIGNS.SALES_SETTINGS.NOT_CONFIGURED')
              "
              class="!mb-0 font-mono text-xs"
            />
          </label>
        </div>

        <Button
          :label="$t('REPORT.CAMPAIGNS.SALES_SETTINGS.ROTATE')"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-ri-key-2-line"
          class="self-start"
          @click="rotateSecret(provider.key)"
        />

        <div
          v-if="revealedSecret && revealedSecret.provider === provider.key"
          class="text-xs bg-n-amber-3 text-n-amber-11 rounded-xl p-2 flex flex-col gap-1"
          role="status"
        >
          <span>{{ $t('REPORT.CAMPAIGNS.SALES_SETTINGS.SECRET_ONCE') }}</span>
          <code class="font-mono break-all">{{ revealedSecret.secret }}</code>
        </div>
      </div>

      <Button
        :label="
          saving
            ? $t('REPORT.CAMPAIGNS.SALES_SETTINGS.SAVING')
            : $t('REPORT.CAMPAIGNS.SALES_SETTINGS.SAVE')
        "
        size="sm"
        class="self-start"
        :is-loading="saving"
        :disabled="saving"
        @click="save"
      />
    </template>
  </div>
</template>
